/**
 * Learning Profile Context
 * Provides learning profile state and onboarding management across the app
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useLearningProfile, useProfileSummary, useOnboardingState } from '@/hooks/use-learning-profile';
import { useAuth } from '@/lib/auth';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import type { LearningProfile, ProfileSummary, OnboardingState } from '@/types/learning-profile';

interface LearningProfileContextType {
  // Profile data
  profile: LearningProfile | null | undefined;
  summary: ProfileSummary | null | undefined;
  isLoading: boolean;

  // Onboarding
  onboardingState: OnboardingState;
  showOnboarding: boolean;
  triggerOnboarding: () => void;
  dismissOnboarding: () => void;

  // Helpers
  isProfileComplete: boolean;
  profileCompleteness: number;
}

const LearningProfileContext = createContext<LearningProfileContextType | undefined>(undefined);

interface LearningProfileProviderProps {
  children: ReactNode;
}

export function LearningProfileProvider({ children }: LearningProfileProviderProps) {
  const { user, isAuthResolved } = useAuth();
  const { data: profile, isLoading: profileLoading } = useLearningProfile();
  const { data: summary, isLoading: summaryLoading } = useProfileSummary();
  const { state: onboardingState, isLoading: onboardingLoading } = useOnboardingState();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);

  const isLoading = profileLoading || summaryLoading || onboardingLoading || !isAuthResolved;

  // Check if we should show onboarding on mount
  useEffect(() => {
    if (!isLoading && profile && user && !hasCheckedOnboarding) {
      setHasCheckedOnboarding(true);

      // Show onboarding if:
      // 1. Profile exists but onboarding not completed and not skipped
      // 2. User is authenticated
      // 3. Current status is still 'unknown' (never answered first question)
      const shouldShowOnboarding =
        !profile.onboarding_completed &&
        !profile.onboarding_skipped &&
        profile.current_status === 'unknown';

      if (shouldShowOnboarding) {
        // Small delay to let the page load first
        setTimeout(() => {
          setShowOnboarding(true);
        }, 500);
      }
    }
  }, [isLoading, profile, user, hasCheckedOnboarding]);

  const triggerOnboarding = useCallback(() => {
    setShowOnboarding(true);
  }, []);

  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  const isProfileComplete = profile?.onboarding_completed ?? false;
  const profileCompleteness = profile?.profile_completeness ?? 0;

  const value: LearningProfileContextType = {
    profile,
    summary,
    isLoading,
    onboardingState,
    showOnboarding,
    triggerOnboarding,
    dismissOnboarding,
    isProfileComplete,
    profileCompleteness,
  };

  return (
    <LearningProfileContext value={value}>
      {children}
    </LearningProfileContext>
  );
}

/**
 * Host for the OnboardingModal. Must be rendered inside <BrowserRouter>
 * because the modal calls useNavigate(). Consumes LearningProfileContext.
 */
export function OnboardingModalHost() {
  const { showOnboarding, dismissOnboarding } = useLearningProfileContext();
  if (!showOnboarding) return null;
  return <OnboardingModal open={showOnboarding} onComplete={dismissOnboarding} />;
}

export function useLearningProfileContext() {
  const context = useContext(LearningProfileContext);
  if (context === undefined) {
    throw new Error('useLearningProfileContext must be used within LearningProfileProvider');
  }
  return context;
}

export default LearningProfileContext;
